// Dashboard card surfacing pending auto-pilot suggestions (REC-073).
// Coach users only — visibility is driven by the Firestore subscription
// which returns empty for non-Coach accounts.

import { useEffect, useState } from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import {
  resolveAutoPilotSuggestion,
  subscribeToAutoPilotSuggestions,
  type AutoPilotSuggestion,
} from '@shared/services/recalibration-api';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS, RECALIBRATION_FLAGS } from '@shared/constants';
import { useFeatureFlagStore } from '@shared/state';
import { THICK_TILE_SHADOW } from '../dashboard/tileStyles';

interface Props {
  userId: string;
}

export function AutoPilotCard({ userId }: Props) {
  const [suggestions, setSuggestions] = useState<AutoPilotSuggestion[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const autoPilotEnabled = useFeatureFlagStore((s) => s.isEnabled(RECALIBRATION_FLAGS.AUTO_PILOT));

  useEffect(() => {
    const unsub = subscribeToAutoPilotSuggestions(userId, setSuggestions);
    return () => unsub();
  }, [userId]);

  useEffect(() => {
    if (suggestions.length === 0) return;
    const latest = suggestions[0];
    trackEvent({
      name: ANALYTICS_EVENTS.RECAL_AUTO_PILOT_SUGGESTION_SHOWN,
      properties: {
        ...getBaseProperties(),
        suggestionId: latest.suggestionId,
        dimension: latest.dimension,
      },
    });
  }, [suggestions]);

  if (!autoPilotEnabled || suggestions.length === 0) return null;

  const top = suggestions[0];

  async function onDecision(decision: 'accept' | 'dismiss') {
    if (busyId) return;
    setBusyId(top.suggestionId);
    try {
      await resolveAutoPilotSuggestion({
        suggestionId: top.suggestionId,
        decision,
      });
      if (decision === 'accept') {
        trackEvent({
          name: ANALYTICS_EVENTS.RECAL_AUTO_PILOT_ACCEPTED,
          properties: {
            ...getBaseProperties(),
            suggestionId: top.suggestionId,
            dimension: top.dimension,
          },
        });
      }
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div
      className="rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(108,92,231,0.08), rgba(240,165,0,0.06))',
        border: '1px solid rgba(108,92,231,0.25)',
        boxShadow: THICK_TILE_SHADOW,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'rgba(240,165,0,0.12)' }}
        >
          <Sparkles size={16} className="text-gold-400" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
            Auto-pilot · Coach
          </div>
          <p className="text-[13px] text-content leading-relaxed m-0 mb-3">
            {top.cardSentence}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDecision('accept')}
              disabled={!!busyId}
              className="rounded-lg text-xs font-semibold disabled:opacity-40"
              style={{
                padding: '6px 12px',
                background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
                color: '#fff',
              }}
            >
              <Check size={12} className="inline mr-1" /> Confirm
            </button>
            <button
              onClick={() => onDecision('dismiss')}
              disabled={!!busyId}
              className="text-xs text-content-muted hover:text-content disabled:opacity-40"
              style={{ padding: '6px 10px' }}
            >
              <X size={12} className="inline mr-1" /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
